/**
 * Registration-link checks for meeting links (ENG-262).
 *
 * Only Zoom's own registration pages are blocked. The agent cannot use them: the
 * link it needs is the per-attendee Join link in Zoom's confirmation email, and
 * 1 of 22 bookings on a Zoom registration page ever succeeded. Other registration
 * pages are often email-gated and joinable — Barclays' `/events/register/` links
 * get the agent in with the registered email alone — so they only get a warning
 * and the agent's post-booking check decides.
 */

// Any company subdomain of zoom.us / zoom.com, then /webinar/register or
// /meeting/register. The leading class keeps `notzoom.us` from matching, and the
// trailing one keeps `/registered` out.
const ZOOM_REGISTRATION_PAGE =
  /(?:^|[^a-z0-9.-])(?:[a-z0-9-]+\.)*zoom\.(?:us|com)\/(?:webinar|meeting)\/register(?:[/?#&]|$)/i

// "register", "registration", "Registration" and friends, in any case.
const REGISTRATION_WORD = /regist/i

export const ZOOM_REGISTRATION_LINK_MESSAGE =
  "This is a Zoom registration page, which the meeting agent can't join. Paste the Join link from Zoom's registration confirmation email instead (it starts with https://<company>.zoom.us/w/)."

export const REGISTRATION_LINK_WARNING =
  'This looks like a registration link. The meeting agent checks it after you submit and will let you know if it needs a direct join link.'

export enum MeetingLinkBlockReason {
  UrlBlacklist = 'url_blacklist',
  ZoomRegistrationPage = 'zoom_registration_page',
}

const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/**
 * The link as pasted plus its percent-decoded forms. Outlook SafeLinks and
 * Proofpoint carry the real URL percent-encoded in their own query string, and
 * some gateways encode it twice.
 */
const candidateForms = (link: string): string[] => {
  const once = safeDecode(link)
  return [link, once, safeDecode(once)]
}

export const isZoomRegistrationLink = (link: string | null | undefined): boolean => {
  if (!link) return false
  return candidateForms(link.trim()).some((form) => ZOOM_REGISTRATION_PAGE.test(form))
}

/** True for registration-looking links that are allowed through with a warning. */
export const needsRegistrationLinkWarning = (link: string | null | undefined): boolean => {
  if (!link || isZoomRegistrationLink(link)) return false
  return candidateForms(link.trim()).some((form) => REGISTRATION_WORD.test(form))
}
